import React, { ReactNode, useMemo } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { DraggableAttributes, UniqueIdentifier } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

export interface SortableItemProps {
  id: UniqueIdentifier;
  children: (props: {
    dragHandleListeners: SyntheticListenerMap | undefined;
    dragHandleAttributes: DraggableAttributes;
    isDragging: boolean;
  }) => ReactNode;
  className?: string;
  containerId: UniqueIdentifier | null;
  disabled?: boolean;
  handle?: boolean;
}

export const SortableItem: React.FC<SortableItemProps> = ({
  id,
  children,
  className,
  containerId,
  disabled = false,
  handle = false,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id,
    data: {
      type: "item",
      containerId,
    },
    disabled,
  });

  // Memoize the child props to prevent recreation
  const childProps = useMemo(
    () => ({
      dragHandleListeners: listeners,
      dragHandleAttributes: attributes,
      isDragging,
    }),
    [listeners, attributes, isDragging]
  );

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex select-none",
        !handle && !disabled && "cursor-grab active:cursor-grabbing",
        isDragging && "border-blue-300 opacity-50 shadow-md",
        disabled && "cursor-not-allowed",
        className
      )}
      {...(handle ? attributes : {})}
      {...(handle ? {} : listeners)}
      data-item-id={id}
      data-container-id={containerId}
    >
      {children(childProps)}
    </div>
  );
};
